export interface BonfireStorageState<T> {
  [prop: string]: {
    [key: string]: T;
  };
}

const BONFIRE_STORAGE_KEY = "BonfireLS";

export function clearPersistedUI(prop: string) {
  const persisted = window.sessionStorage.getItem(BONFIRE_STORAGE_KEY);
  const state = persisted !== null ? JSON.parse(persisted) : {};
  const newState = {
    ...state,
    [prop]: undefined,
  };
  window.sessionStorage.setItem(BONFIRE_STORAGE_KEY, JSON.stringify(newState));
}

interface Storage {
  key: string;
  prop: string;
  useLocalStorage?: boolean; // Defaults to sessionStorage if omitted
}
interface SetStorage<T> extends Storage {
  data: T;
}

export function setStorage<T>({
  key,
  prop,
  data,
  useLocalStorage,
}: SetStorage<T>) {
  const storage = useLocalStorage ? window.localStorage : window.sessionStorage;
  const persisted = storage.getItem(BONFIRE_STORAGE_KEY);
  const state: BonfireStorageState<T> =
    persisted !== null ? JSON.parse(persisted) : { [key]: {} };
  const newState = {
    ...state,
    [key]: { ...state[key], [prop]: data },
  };
  storage.setItem(BONFIRE_STORAGE_KEY, JSON.stringify(newState));
}

export function getStorage<T>({
  key,
  prop,
  useLocalStorage,
}: Storage): T | undefined {
  const storage = useLocalStorage ? window.localStorage : window.sessionStorage;
  const persisted = storage.getItem(BONFIRE_STORAGE_KEY);
  const state: BonfireStorageState<T> =
    persisted !== null ? JSON.parse(persisted) : { [key]: {} };
  return state[key] ? state[key][prop] : undefined;
}

export function clearStorage({ key, useLocalStorage }: Omit<Storage, "prop">) {
  const storage = useLocalStorage ? window.localStorage : window.sessionStorage;
  const persisted = storage.getItem(BONFIRE_STORAGE_KEY);
  const state = persisted !== null ? JSON.parse(persisted) : {};
  const newState = {
    ...state,
    [key]: undefined,
  };
  storage.setItem(BONFIRE_STORAGE_KEY, JSON.stringify(newState));
}

type PersistConfig = { key: string; prop: string; useLocalStorage?: boolean };
export function getPersisted<T>(config: PersistConfig, initial: T) {
  const d: T | undefined = getStorage(config);
  if (d !== undefined) {
    return d;
  }
  return initial;
}

export function setPersisted<T>(config: PersistConfig, data: T) {
  setStorage({ ...config, data });
}
